import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Phone, Mail, ArrowRight, Check, Star,
  Zap, Eye, Network, Lock, Wifi, Bell, Server, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { getServices, getCities } from '../lib/api';

const iconMap = {
  Zap, Eye, Network, Lock, Wifi, Bell, Server, Settings
};

// City-specific SEO content
const cityContent = {
  orlando: {
    name: "Orlando",
    state: "FL",
    region: "Central Florida",
    population: "2.5 million",
    description: "Orlando, the heart of Central Florida, is home to world-renowned theme parks, a thriving tech industry, and countless businesses requiring top-tier low voltage infrastructure.",
    description_es: "Orlando, el corazón de Florida Central, alberga parques temáticos de renombre mundial, una próspera industria tecnológica y numerosas empresas que requieren infraestructura de bajo voltaje de primera clase.",
    nearbyAreas: ["Kissimmee", "Sanford", "Winter Park", "Lake Mary", "Altamonte Springs", "Oviedo", "Daytona Beach", "Melbourne"],
    keywords: ["fiber optic Orlando", "CCTV installation Orlando FL", "low voltage contractor Orlando", "network cabling Orlando Florida"],
    image: "https://images.unsplash.com/photo-1575089976121-8ed7b2a54265?w=1200"
  },
  miami: {
    name: "Miami",
    state: "FL",
    region: "South Florida",
    population: "6 million",
    description: "Miami, the vibrant gateway to Latin America, features a dynamic business environment with high demand for advanced security systems, fiber optics, and modern networking solutions.",
    description_es: "Miami, la vibrante puerta de entrada a América Latina, cuenta con un entorno empresarial dinámico con alta demanda de sistemas de seguridad avanzados, fibra óptica y soluciones de red modernas.",
    nearbyAreas: ["Fort Lauderdale", "Hollywood", "Boca Raton", "Coral Gables", "Miami Beach", "Hialeah", "Homestead", "Key West"],
    keywords: ["fiber optic Miami", "CCTV installation Miami FL", "low voltage contractor Miami", "security systems Miami Florida"],
    image: "https://images.unsplash.com/photo-1533106497176-45ae19e68ba2?w=1200"
  },
  tampa: {
    name: "Tampa",
    state: "FL",
    region: "West Central Florida",
    population: "3 million",
    description: "Tampa Bay is one of Florida's fastest-growing metropolitan areas, with businesses across healthcare, finance, and technology requiring reliable low voltage solutions.",
    description_es: "Tampa Bay es una de las áreas metropolitanas de más rápido crecimiento en Florida, con empresas de salud, finanzas y tecnología que requieren soluciones de bajo voltaje confiables.",
    nearbyAreas: ["St. Petersburg", "Clearwater", "Brandon", "Lakeland", "Sarasota", "Bradenton", "Plant City", "Wesley Chapel"],
    keywords: ["fiber optic Tampa", "CCTV installation Tampa FL", "low voltage contractor Tampa", "structured cabling Tampa Florida"],
    image: "https://images.unsplash.com/photo-1564507592333-c60657eea523?w=1200"
  },
  jacksonville: {
    name: "Jacksonville",
    state: "FL",
    region: "Northeast Florida",
    population: "1.5 million",
    description: "Jacksonville, the largest city by area in the contiguous US, is a major logistics hub with growing demand for enterprise-grade networking and security infrastructure.",
    description_es: "Jacksonville, la ciudad más grande por área en los Estados Unidos continentales, es un importante centro logístico con una creciente demanda de infraestructura de redes y seguridad empresarial.",
    nearbyAreas: ["St. Augustine", "Orange Park", "Fleming Island", "Ponte Vedra", "Fernandina Beach", "Jacksonville Beach", "Gainesville"],
    keywords: ["fiber optic Jacksonville", "CCTV installation Jacksonville FL", "low voltage contractor Jacksonville", "network installation Jacksonville"],
    image: "https://images.unsplash.com/photo-1569974507005-6dc61f97fb5c?w=1200"
  },
  "west-palm-beach": {
    name: "West Palm Beach",
    state: "FL",
    region: "Southeast Florida",
    population: "1.5 million",
    description: "West Palm Beach and the Palm Beach County area feature luxury developments, corporate headquarters, and high-end commercial properties requiring premium low voltage solutions.",
    description_es: "West Palm Beach y el área del condado de Palm Beach cuentan con desarrollos de lujo, sedes corporativas y propiedades comerciales de alta gama que requieren soluciones de bajo voltaje premium.",
    nearbyAreas: ["Boca Raton", "Jupiter", "Palm Beach Gardens", "Wellington", "Boynton Beach", "Delray Beach", "Port St. Lucie", "Stuart"],
    keywords: ["fiber optic West Palm Beach", "CCTV installation Palm Beach FL", "low voltage contractor West Palm Beach", "security systems Palm Beach"],
    image: "https://images.unsplash.com/photo-1605723517503-3cadb5818a0c?w=1200"
  },
  naples: {
    name: "Naples",
    state: "FL",
    region: "Southwest Florida",
    population: "400,000",
    description: "Naples, known for its affluent communities and pristine beaches, serves high-end residential and commercial clients requiring sophisticated low voltage systems.",
    description_es: "Naples, conocida por sus comunidades prósperas y playas prístinas, atiende a clientes residenciales y comerciales de alto nivel que requieren sistemas de bajo voltaje sofisticados.",
    nearbyAreas: ["Fort Myers", "Cape Coral", "Bonita Springs", "Marco Island", "Estero", "Lehigh Acres", "Punta Gorda"],
    keywords: ["fiber optic Naples FL", "CCTV installation Naples Florida", "low voltage contractor Naples", "security systems Southwest Florida"],
    image: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=1200"
  }
};

export default function CityPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const city = cityContent[slug] || cityContent.orlando;
  const currentLanguage = language || 'en';
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesRes = await getServices();
        setServices(servicesRes.data || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const pageTitle = currentLanguage === 'es' 
    ? `Servicios de Bajo Voltaje en ${city.name}, Florida | IQES`
    : `Low Voltage Services in ${city.name}, Florida | IQES`;
  
  const pageDescription = currentLanguage === 'es'
    ? `IQES ofrece instalación profesional de fibra óptica, CCTV, cableado estructurado y más en ${city.name}, FL. Servicio 24/7. Cotización.`
    : `IQES provides professional fiber optic, CCTV, structured cabling, and low voltage installation in ${city.name}, FL. 24/7 service. Free quotes.`;

  // Schema.org LocalBusiness markup
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `IQES - ${city.name}`,
    "description": currentLanguage === 'es' ? city.description_es : city.description,
    "url": `https://lowvolt-test.preview.emergentagent.com/locations/${slug}`,
    "telephone": "+1-786-981-3760",
    "email": "info@iqes.com",
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "containedInPlace": {
        "@type": "State",
        "name": "Florida"
      }
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": city.name === "Orlando" ? 28.5383 : 25.7617,
        "longitude": city.name === "Orlando" ? -81.3792 : -80.1918
      },
      "geoRadius": "150 mi"
    },
    "priceRange": "$$",
    "openingHours": "Mo-Fr 08:00-18:00",
    "sameAs": [
      "https://www.facebook.com/iqes",
      "https://www.linkedin.com/company/iqes"
    ]
  };

  // Get title and description as strings
  const titleText = currentLanguage === 'es' 
    ? "Servicios de Bajo Voltaje en " + city.name + ", Florida | IQES"
    : "Low Voltage Services in " + city.name + ", Florida | IQES";
  
  const descText = currentLanguage === 'es'
    ? "IQES ofrece instalación profesional de fibra óptica, CCTV, cableado estructurado y más en " + city.name + ", FL. Servicio 24/7. Cotización."
    : "IQES provides professional fiber optic, CCTV, structured cabling, and low voltage installation in " + city.name + ", FL. 24/7 service. Free quotes.";

  const canonicalUrl = `https://iqeslowvoltage.com/locations/${slug}`;

  // Update document title via effect for compatibility
  useEffect(() => {
    document.title = titleText;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', descText);
    }
    
    // Update OG tags
    const updateMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    updateMeta('og:title', titleText);
    updateMeta('og:description', descText);
    updateMeta('og:type', 'website');
    updateMeta('og:image', city.image);
    
    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
    
    // Add JSON-LD schema
    let schemaScript = document.querySelector('script[type="application/ld+json"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schemaData);
    
  }, [titleText, descText, city.image, canonicalUrl, schemaData]);

  return (
    <>
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${city.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/70" />
          
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <div className="flex items-center gap-2 text-primary mb-4">
                <MapPin className="w-5 h-5" />
                <span className="text-sm font-medium">{city.region}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                {currentLanguage === 'es' 
                  ? `Servicios de Bajo Voltaje en ${city.name}, FL`
                  : `Low Voltage Services in ${city.name}, FL`}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                {currentLanguage === 'es' ? city.description_es : city.description}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/quote">
                  <Button className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg">
                    {currentLanguage === 'es' ? 'Cotización' : 'Quote'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+17869813760">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg">
                    <Phone className="mr-2 w-5 h-5" />
                    (786) 574-2905
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-card/50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
                {currentLanguage === 'es' 
                  ? `Nuestros Servicios en ${city.name}`
                  : `Our Services in ${city.name}`}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {currentLanguage === 'es'
                  ? 'Soluciones profesionales de infraestructura de bajo voltaje para empresas y residencias'
                  : 'Professional low voltage infrastructure solutions for businesses and residences'}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.slice(0, 6).map((service, idx) => {
                const IconComponent = iconMap[service.icon] || Zap;
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="bg-card border-border/50 hover:border-primary/50 transition-all h-full">
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {currentLanguage === 'es' ? service.name_es : service.name}
                        </h3>
                        <p className="text-muted-foreground mb-4 line-clamp-2">
                          {currentLanguage === 'es' ? service.description_es : service.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-primary font-semibold">
                            {currentLanguage === 'es' ? 'Desde' : 'From'} ${service.base_price?.toLocaleString()}
                          </span>
                          <Link to={`/services/${service.slug}`}>
                            <Button variant="ghost" size="sm" className="text-primary">
                              {currentLanguage === 'es' ? 'Ver más' : 'Learn more'}
                              <ArrowRight className="ml-1 w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Service Area Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
                  {currentLanguage === 'es' 
                    ? `Áreas que Servimos cerca de ${city.name}`
                    : `Areas We Serve near ${city.name}`}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {currentLanguage === 'es'
                    ? `Además de ${city.name}, ofrecemos nuestros servicios en un radio de 150 millas, incluyendo:`
                    : `In addition to ${city.name}, we offer our services within a 150-mile radius, including:`}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {city.nearbyAreas.map((area, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-foreground">{area}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-8 border border-border/50"
              >
                <h3 className="text-2xl font-heading font-bold text-white mb-6">
                  {currentLanguage === 'es' ? '¿Por qué elegir IQES?' : 'Why Choose IQES?'}
                </h3>
                <div className="space-y-4">
                  {[
                    { en: 'Licensed & Insured Contractors', es: 'Contratistas Licenciados y Asegurados' },
                    { en: 'Certified Fiber Optic Specialists', es: 'Especialistas Certificados en Fibra Óptica' },
                    { en: '24/7 Emergency Support', es: 'Soporte de Emergencia 24/7' },
                    { en: 'Estimates', es: 'Presupuestos' },
                    { en: '150+ Mile Service Radius', es: 'Radio de Servicio de 150+ Millas' },
                    { en: 'Satisfaction Guaranteed', es: 'Satisfacción Garantizada' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-foreground">{currentLanguage === 'es' ? item.es : item.en}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary/5">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
                {currentLanguage === 'es' 
                  ? `¿Listo para empezar su proyecto en ${city.name}?`
                  : `Ready to Start Your Project in ${city.name}?`}
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                {currentLanguage === 'es'
                  ? 'Contáctenos hoy para una consulta gratuita y presupuesto sin compromiso.'
                  : 'Contact us today for a free consultation and no-obligation quote.'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/quote">
                  <Button className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg">
                    {currentLanguage === 'es' ? 'Obtener Cotización' : 'Get a Quote'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg">
                    <Mail className="mr-2 w-5 h-5" />
                    {currentLanguage === 'es' ? 'Contáctenos' : 'Contact Us'}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
